// ------------------------------------------------------------
// Core Types for Type-Safe Experiments
// ------------------------------------------------------------

type ExperimentName = string;
type ExperimentUserGroupName = string;
export type ExperimentParameterName = string;
export type ExperimentParameterValue =
    // These are what Statsig supports for experiment customer parameters
    | string
    | number
    | boolean
    | readonly string[]
    | readonly number[]
    | Record<string, unknown>;

/**
 * A Statsig Experiment definition
 * @param name - The unique identifier for the experiment
 * @param userGroups - List of user group names (e.g. 'Control', 'Test', 'Treatment A')
 * @param parameters - Record of experiment parameters (if any) - each parameter has a key and possible values
 * @see https://console.statsig.com/7HZyy2rsM7PvLX6WrgPQUo/experiments
 *
 */
interface ExperimentDefinition {
    name: ExperimentName;
    userGroups: ExperimentUserGroupName[];
    parameters?: Record<ExperimentParameterName, ExperimentParameterDefinition>;
}

/**
 * A Statsig Experiment parameter, which are custom defined within each experiment.
 * Each experiment group (i.e. 'Control', 'Test', 'TreatmentA', 'TreatmentB', etc) may have a different value
 * for each parameter. These values can be used to alter the experience/behavior for each experiment group.
 * @see https://console.statsig.com/7HZyy2rsM7PvLX6WrgPQUo/experiments
 */
interface ExperimentParameterDefinition {
    key: ExperimentParameterName;
    values: readonly ExperimentParameterValue[];
}

// ------------------------------------------------------------
// Define all experiments here
// ------------------------------------------------------------

/**
 * A Proof of Concept experiment
 * @see https://console.statsig.com/7HZyy2rsM7PvLX6WrgPQUo/experiments/dummy_experiment_20250910/setup
 */
export const DummyExperiment20250910 = {
    name: 'dummy_experiment_20250910',
    userGroups: ['Group_Gated', 'Group_NOT_Gated'],
    parameters: {
        experiment_param: {
            key: 'experiment_param',
            values: ['control', 'test'],
        },
    },
} as const satisfies ExperimentDefinition;
export type DummyExperiment20250910_UserGroup =
    (typeof DummyExperiment20250910.userGroups)[number];
export type DummyExperiment20250910_ExperimentParam_Value =
    (typeof DummyExperiment20250910.parameters.experiment_param.values)[number];

/**
 * An Onboarding experiment to test the upper limit of scheduling Discovery session with no prerequisites
 * @see https://console.statsig.com/7HZyy2rsM7PvLX6WrgPQUo/experiments
 */
export const DiscoveryBarrier = {
    name: 'discoverybarrier',
    userGroups: [
        'Control_RequireCompletedProfileSteps',
        'Test_DiscoveryBeforeProfileSteps',
    ],
} as const satisfies ExperimentDefinition;
export type DiscoveryBarrier_UserGroup =
    (typeof DiscoveryBarrier.userGroups)[number];

export const CmmArtifactFinalPageExperiment = {
    name: 'cmm_artifact_final_page',
    userGroups: ['Guides', 'System', 'Account'],
    parameters: {
        experiment_param: {
            key: 'experiment_param',
            values: ['guides', 'system', 'account'],
        },
    },
} as const satisfies ExperimentDefinition;
export type CmmArtifactFinalPageExperiment_UserGroup =
    (typeof CmmArtifactFinalPageExperiment.userGroups)[number];
export type CmmArtifactFinalPageExperiment_ExperimentParam_Value =
    (typeof CmmArtifactFinalPageExperiment.parameters.experiment_param.values)[number];
